import React from "react";
import { FeatureDetail, type FeatureStaticData } from "./FeatureDetail";
import { RegionOverview } from "./RegionOverview";
import type { MapFeature, MapFeatureProperties, MapFeatureCollection, MapSearchResult } from "../../api/map";
import type { FilterType } from "./FilterBar";

// A representative [lng, lat] for any geometry so "nearby" can be sorted by
// actual distance rather than the arbitrary order features arrive in.
function representativePoint(geom: MapFeature["geometry"]): [number, number] | null {
  const coords = geom?.coordinates;
  if (!Array.isArray(coords)) return null;
  // Point → [lng, lat]; LineString → first vertex; MultiLineString/Polygon →
  // first vertex of the first ring/line. Descend until we hit two numbers.
  let node: unknown = coords;
  while (Array.isArray(node) && Array.isArray(node[0])) node = node[0];
  if (Array.isArray(node) && typeof node[0] === "number" && typeof node[1] === "number") {
    return [node[0], node[1]];
  }
  return null;
}

// Squared planar distance is enough for ordering nearby features at the scales
// shown here — no need for great-circle accuracy just to rank the closest few.
function distanceSq(a: [number, number], b: [number, number]): number {
  const dx = a[0] - b[0];
  const dy = a[1] - b[1];
  return dx * dx + dy * dy;
}

interface MapInspectorProps {
  selected: MapFeatureProperties | null;
  features: MapFeatureCollection | null;
  filter: FilterType;
  landmarkCategories: string[];
  onSelect: (f: MapFeatureProperties) => void;
  onDeselect: () => void;
  onFocusMap?: (f: MapFeatureProperties) => void;
  onSearchSelect?: (r: MapSearchResult) => void;
  regionName?: string;
  regionCoveragePct?: number;
  /** True when rendered as the mobile bottom sheet — FeatureDetail switches
      to its compact header (title at top, X close) to save vertical space. */
  sheet?: boolean;
  /** Demo mode (landing page map): fixed data per feature, no requests. */
  staticDataFor?: (f: MapFeatureProperties) => FeatureStaticData;
}

export function MapInspector({
  selected,
  features,
  filter,
  landmarkCategories,
  onSelect,
  onDeselect,
  onFocusMap,
  onSearchSelect,
  regionName,
  regionCoveragePct,
  sheet,
  staticDataFor,
}: MapInspectorProps) {
  const selectedPoint = selected
    ? representativePoint(
        (features?.features ?? []).find((f) => f.properties.id === selected.id)?.geometry ??
          ({ type: "", coordinates: null } as MapFeature["geometry"]),
      )
    : null;

  const nearby =
    selected && selectedPoint
      ? (features?.features ?? [])
          .filter((f) => f.properties.type === selected.type && f.properties.id !== selected.id)
          .map((f) => ({ f, point: representativePoint(f.geometry) }))
          .filter((x): x is { f: MapFeature; point: [number, number] } => x.point !== null)
          .sort((a, b) => distanceSq(a.point, selectedPoint) - distanceSq(b.point, selectedPoint))
          .slice(0, 3)
          .map((x) => x.f.properties)
      : selected
        ? (features?.features ?? [])
            .map((f) => f.properties)
            .filter((f) => f.type === selected.type && f.id !== selected.id)
            .slice(0, 3)
        : [];

  return (
    <aside
      className="map-inspector"
      style={{
        width: 360,
        flex: "0 0 360px",
        borderLeft: "1px solid var(--border-subtle)",
        background: "var(--surface-card)",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {selected ? (
        <FeatureDetail
          feature={selected}
          nearby={nearby}
          onBack={onDeselect}
          onSelect={onSelect}
          onFocusMap={onFocusMap}
          sheet={sheet}
          staticData={staticDataFor?.(selected)}
        />
      ) : (
        <RegionOverview
          filter={filter}
          landmarkCategories={landmarkCategories}
          features={features}
          onSelect={onSelect}
          onSearchSelect={onSearchSelect}
          regionName={regionName}
          regionCoveragePct={regionCoveragePct}
        />
      )}
    </aside>
  );
}
